


























import Vue, { PropType } from 'vue';
import { difference } from 'lodash';

// Services and Types
import { RestXReactiveData } from '~/utils/types';
import { SchoolVerification, SchoolVerificationState } from '~/services/types/school';
import { getDurationEoD } from '~/utils/date-fns/duration';
import SchoolVerificationAlertHelper, { AlertPage } from './SchoolVerificationAlertHelper';
import { Candidate } from '~/services/types/candidate';

// Components
import Alert from '~/components/builders/Alert.vue';
import IconBtn from '~/components/builders/IconBtn.vue';

export default Vue.extend({
    name: 'SchoolVerificationAlert',
    components: { Alert, IconBtn },
    props: {
        delay: {
            type: Number,
            default: 1000,
        },
        alertPage: {
            type: String as PropType<AlertPage>,
            default: 'jobs',
        },
        showMethod: {
            type: String as PropType<'someUnverifiedOrNoCSP' | 'someUnverified'>,
            default: 'someUnverifiedOrNoCSP',
        },
    },
    data() {
        return {
            loaded: false,
            startTime: Date.now(),
            isDismissed: false,
        };
    },
    computed: {
        loading(): boolean {
            return this.schoolVerifications.loading;
        },
        schoolVerifications(): RestXReactiveData<SchoolVerification[]> {
            return (this as any).$interfaces.university.connection.get({ reactive: true });
        },
        hasNoSchools(): boolean {
            const candidate: Candidate | null = this.$store.getters['profileModule/getCandidate']();
            if (!candidate) {
                return false;
            }

            return candidate.studyPrograms.length === 0;
        },
        hasSomeUnverifiedSchools(): boolean {
            const candidate: Candidate | null = this.$store.getters['profileModule/getCandidate']();
            if (!candidate) {
                return false;
            }

            const verifiableStudyPrograms = candidate.studyPrograms.filter(
                sp => sp.studyProgram.schoolId
            );
            const verifiableSchoolIds = verifiableStudyPrograms.map(
                sp => sp.studyProgram.schoolId as number
            );
            const unverifiedSchoolIds = this.schoolVerifications.data
                .filter(
                    school =>
                        school.state !== SchoolVerificationState.verified &&
                        school.state !== SchoolVerificationState.ignored
                )
                .map(school => school.schoolId)
                .concat(
                    difference<number>(
                        verifiableSchoolIds,
                        this.schoolVerifications.data.map(school => school.schoolId)
                    )
                );

            return unverifiedSchoolIds.length > 0;
        },
        isAuthenticated(): boolean {
            return (this as any).$interfaces.logInModule.isLoggedIn();
        },
        alertText(): string {
            switch (this.alertPage as AlertPage) {
                case 'events':
                case 'jobs':
                    return 'components.miscellaneous.schoolVerificationAlert.jobText';
                case 'resources':
                    return 'components.miscellaneous.schoolVerificationAlert.resourcesText';
                case 'calendar':
                    return 'components.miscellaneous.schoolVerificationAlert.calendarText';
                default:
                    return 'components.miscellaneous.schoolVerificationAlert.defaultText';
            }
        },

        show(): boolean {
            return (
                this.isAuthenticated &&
                !this.isDismissed &&
                this.loaded &&
                (this.showMethod === 'someUnverifiedOrNoCSP'
                    ? this.hasSomeUnverifiedSchools || this.hasNoSchools
                    : this.hasSomeUnverifiedSchools)
            );
        },
    },
    methods: {
        goToProfileEducation() {
            this.$router.push({
                name: 'profile',
                query: { scrollTo: 'educations' },
            });
        },
        scheduleReadiness() {
            const now = Date.now();
            const timePassed = now - this.startTime;
            const timeLeft = this.delay - timePassed;

            if (timeLeft > 0) {
                setTimeout(() => {
                    this.loaded = true;
                }, timeLeft);
            } else {
                this.loaded = true;
            }
        },
        dismiss() {
            const durationEoD = getDurationEoD();
            SchoolVerificationAlertHelper.dismissAlert(this.alertPage as AlertPage, durationEoD);
            this.isDismissed = true;
        },
    },
    mounted() {
        this.isDismissed = SchoolVerificationAlertHelper.isAlertDismissed(
            this.alertPage as AlertPage
        );
    },
    watch: {
        loading: {
            handler(newLoading: boolean, oldLoading: boolean) {
                if (newLoading === false) {
                    this.scheduleReadiness();
                }
            },
            immediate: true,
        },
    },
});
